import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard, Divider } from "@/components/ds";
import { Building, Check } from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";

const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;

// ─── Capability List ────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Deals & Pipeline",
    icon: "Briefcase",
    items: [
      "Create deals from live conversations the moment a visitor shows buying intent — deal is automatically linked to the conversation and contact",
      "Move deals through custom pipeline stages (drag Kanban or AI-driven stage updates)",
      "AI detects buying intent phrases and creates deals without agent involvement",
      "Win/loss tracking with auto-close date and lost-reason capture",
      "Quote builder: add line items from your product catalog, apply per-unit discounts, auto-calculate deal total",
      "Automatic order-to-deal linkage — when a BigCommerce or Shopify order is paid, the linked deal is automatically closed as Won with the order total captured"
    ]
  },
  {
    title: "Contacts & Companies",
    icon: "Users",
    items: [
      "Search any contact by name, email, company, or phone — no external system needed",
      "Full contact profile: name, email, phone, job title, company, location, social links",
      "Company records with firmographic data: industry, size, revenue, employee count",
      "Company ↔ contact linking — see all contacts at a company and all companies a contact belongs to",
      "Contact 360 view: every conversation, deal, activity, and buying signal in one scrollable timeline",
      "AI lead scoring (0–100) — updated daily based on buying signals detected in conversations, open deals, won deals, and conversation frequency"
    ]
  },
  {
    title: "Activities & Tasks",
    icon: "CheckCircle",
    items: [
      "5 activity types: Task, Call, Email, Meeting, Demo — each with due date, priority, and owner",
      "AI schedules follow-ups automatically from natural language: 'call me back Friday' → task created",
      "Complete activities and log outcomes without leaving the conversation",
      "Deal-level activity feed so every team member sees the full follow-up history",
      "Overdue activity alerts surface stale deals before they die",
      "CRM sequences — when a deal moves to a stage (e.g. 'Proposal Sent'), a pre-defined series of tasks is automatically created with staggered due dates"
    ]
  },
  {
    title: "Buying Signals (AI)",
    icon: "Crosshairs",
    items: [
      "Real-time detection of 10 buying signal types during every conversation: BUDGET, TIMELINE, AUTHORITY, PAIN_POINT, COMPETITOR, READY_TO_BUY, FEATURE_INTEREST, REFERRAL, OBJECTION, PRICING_QUESTION",
      "Each signal includes the exact trigger phrase, an AI-generated insight, and a 0–100% confidence score",
      "AI bots can query buying signals mid-conversation and act on high-confidence signals: create deals, escalate to sales, or offer pricing",
      "Signal feed shows all signals across today's conversations — no lead left unworked",
      "Competitors mention Salesforce, HubSpot, or Zendesk? Velaro catches it and logs a COMPETITOR signal automatically"
    ]
  },
  {
    title: "Notes & Knowledge",
    icon: "FileText",
    items: [
      "AI auto-generates post-chat CRM notes with pain points, interests, next steps, and deal readiness score",
      "Manual notes on any deal — visible to every agent who handles the account",
      "@mention teammates in deal notes — they get an instant notification and can view the deal in one click",
      "Notes carry the full conversation context: agent never has to re-read the chat to understand what happened",
      "All notes timestamped and author-attributed (agent or AI)"
    ]
  },
  {
    title: "Deal Intelligence & Health",
    icon: "ShieldCheck",
    items: [
      "Deal Health Score (0–100) — auto-calculated from last activity recency, close date proximity, buying signals, and open critical support tickets. Green / Yellow / Red indicator on every Kanban card",
      "Smart Next Action AI — one-click recommendation of the single most impactful thing to do on a deal: re-engage a stale deal, resolve a blocking support ticket, schedule a demo when signals are high",
      "Deal Audit Trail — every field change logged (who, what, when) — full compliance and coaching record",
      "Revenue Attribution — see which channels (Chat, SMS, IVR, WhatsApp, Email) generate the most closed-won deals and revenue"
    ]
  },
  {
    title: "Templates, Goals & Custom Fields",
    icon: "Settings",
    items: [
      "Deal Templates — pre-configured deal wizards (SaaS Subscription, Professional Services, Hardware Bundle) with default line items and auto-created activities. Spin up a fully-formed deal in one click",
      "Pipeline Quota Goals — set monthly/quarterly/annual revenue targets per rep or team. Real-time attainment tracking with pace indicators (ahead / on-track / behind)",
      "Custom Deal Fields — add any field your team needs: text, number, date, dropdown, checkbox, or currency. Scoped to a specific pipeline or site-wide. Up to 50 fields",
      "Ticket–Deal Bridge — link support tickets to deals. Critical unresolved tickets automatically reduce the deal health score, surfacing customer success risks before they kill the deal"
    ]
  },
  {
    title: "360° Contact Timeline",
    icon: "Clock",
    items: [
      "Unified timeline per contact — every conversation, support ticket, deal creation, deal outcome, activity, buying signal, and email in one chronological feed",
      "Filter timeline by event type: show only tickets, or only deals, or only buying signals",
      "Salesforce charges enterprise rates for this view — Velaro includes it on every plan"
    ]
  }
];


// ─── AI Tools ───────────────────────────────────────────────────────────────

const AI_TOOLS = [
  // Deals (5)
  { name: "native_crm_create_deal", label: "Create deal", group: "Deals", description: "Create a deal linked to the conversation and contact when buying intent is detected." },
  { name: "native_crm_search_deals", label: "Search deals", group: "Deals", description: "Find deals by name, contact, or stage. Returns amount, close date, and pipeline position." },
  { name: "native_crm_get_deal", label: "Get deal details", group: "Deals", description: "Retrieve full deal record: amount, stage, contacts, notes count, and last activity." },
  { name: "native_crm_update_deal", label: "Update deal", group: "Deals", description: "Change stage, amount, or close date. Auto-flags wins and losses." },
  { name: "native_crm_log_activity", label: "Log activity", group: "Deals", description: "Create a task, call, email, meeting, or demo linked to a deal or contact." },
  // Contacts (4)
  { name: "native_crm_create_contact", label: "Create contact", group: "Contacts", description: "Create a new CRM contact from conversation data. Automatically links to the active conversation." },
  { name: "native_crm_get_contact", label: "Get contact", group: "Contacts", description: "Retrieve a contact by ID or email — full profile including company and location." },
  { name: "native_crm_search_contacts", label: "Search contacts", group: "Contacts", description: "Find contacts by name, email, company, or phone number." },
  { name: "native_crm_update_contact", label: "Update contact", group: "Contacts", description: "Update any contact field: name, email, phone, company, job title, or location." },
  // Ticketing (1)
  { name: "native_crm_create_ticket", label: "Create support ticket", group: "Ticketing", description: "Open a support ticket in Velaro's ticketing system. Assigns a ticket number, routes to a team queue, and sends a confirmation receipt." },
  // Companies (4)
  { name: "native_crm_create_company", label: "Create company", group: "Companies", description: "Create a company/account record with industry, size, revenue, and contact info." },
  { name: "native_crm_get_company", label: "Get company", group: "Companies", description: "Retrieve company record with deal count and firmographic data." },
  { name: "native_crm_search_companies", label: "Search companies", group: "Companies", description: "Find companies by name, domain, or industry keyword." },
  { name: "native_crm_update_company", label: "Update company", group: "Companies", description: "Update any field on a company: name, domain, industry, revenue, or employee count." },
  // Activities & Notes (4)
  { name: "native_crm_get_activities", label: "Get activities", group: "Activities & Notes", description: "List open or completed activities for a deal or contact — tasks, calls, meetings." },
  { name: "native_crm_complete_activity", label: "Complete activity", group: "Activities & Notes", description: "Mark a task or activity done and record the outcome." },
  { name: "native_crm_add_note", label: "Add note", group: "Activities & Notes", description: "Attach a note to a deal — captures conversation context for the next agent." },
  { name: "native_crm_get_notes", label: "Get notes", group: "Activities & Notes", description: "Retrieve notes on a deal — full history of what was said and decided." },
  // Deal Detail (4)
  { name: "native_crm_get_deal_contacts", label: "Get deal contacts", group: "Deal Detail", description: "List all stakeholders linked to a deal with their roles (Decision Maker, Influencer, etc.)." },
  { name: "native_crm_link_contact_to_deal", label: "Link contact to deal", group: "Deal Detail", description: "Associate a contact with a deal and assign their stakeholder role." },
  { name: "native_crm_get_deal_activities", label: "Get deal activities", group: "Deal Detail", description: "List all activities scoped to a specific deal." },
  { name: "native_crm_get_deal_line_items", label: "Get deal line items", group: "Deal Detail", description: "Retrieve the quote breakdown — products, quantities, discounts, and totals." },
  // Pipeline / Products / Intelligence (4)
  { name: "native_crm_get_pipelines", label: "Get pipelines", group: "Pipeline & Products", description: "List all active pipelines with stage names and default win probabilities." },
  { name: "native_crm_search_products", label: "Search products", group: "Pipeline & Products", description: "Search the product/service catalog by name, SKU, or category." },
  { name: "native_crm_add_line_item", label: "Add line item", group: "Pipeline & Products", description: "Add a product or custom line item to a deal. Auto-updates deal total." },
  { name: "native_crm_get_buying_signals", label: "Get buying signals", group: "Pipeline & Products", description: "Retrieve AI-detected buying signals (budget, timeline, intent, pain points) from this conversation." },
  // Intelligence (1) — Feature Pack
  { name: "native_crm_suggest_next_action", label: "Suggest next action", group: "AI Intelligence", description: "Analyzes a deal's health score, buying signals, open tickets, and activity recency to recommend the single most impactful next step." }
];

const AI_TOOL_GROUPS = ["Deals", "Contacts", "Companies", "Activities & Notes", "Deal Detail", "Pipeline & Products", "AI Intelligence"];

// ─── Back button ─────────────────────────────────────────────────────────────

export function BackToIntegrationLandingNativeCrm() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
      onClick={() => navigate("/integrations")}
    >
      ← Back to Integrations
    </button>
  );
}

// ─── Capabilities Panel ──────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(true);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0, 1, 3]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <SectionCard className="mb-4 !p-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
        </div>
        <svg className={`w-4 h-4 text-zinc-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <Divider />
          <div className="space-y-2 mt-4">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                >
                  <div className="flex items-center gap-3">
                    <IconByName name={cap.icon} className="w-4 h-4" />
                    <span className="text-sm font-medium text-zinc-700">{cap.title}</span>
                  </div>
                  <svg className={`w-3.5 h-3.5 text-zinc-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="text-xs text-zinc-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      )}
    </SectionCard>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function NativeCrm() {
  const { enableNativeCrm } = useAdminFeatures();
  const [showWizard, setShowWizard] = useState(false);
  const navigate = useNavigate();

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="native-crm"
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Hero banner */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white p-6 mb-4 shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Building className="w-6 h-6" /></div>
              <div>
                <div className="font-bold text-xl">Velaro CRM</div>
                <div className="text-indigo-200 text-sm">Built-in · No external account needed · Always on</div>
              </div>
            </div>
            <p className="text-indigo-100 text-sm max-w-xl mt-3">
              A complete CRM embedded inside Velaro — deals, contacts, companies, activities, buying signals, and a product catalog.
              AI can read and write CRM data during bot conversations in real time.
              No Salesforce. No HubSpot. No external accounts required.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">24 AI tools</span>
              <span className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">Buying signal detection</span>
              <span className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">Zero setup</span>
              <span className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">Contact 360 view</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
              enableNativeCrm ? "bg-green-400/30 text-green-100" : "bg-white/10 text-indigo-200"
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${enableNativeCrm ? "bg-green-300" : "bg-indigo-300"}`} />
              {enableNativeCrm ? "Enabled on this site" : "Not enabled"}
            </div>
            {enableNativeCrm ? (
              <a
                href={`${messagingClientUrl}/crm`}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-white text-indigo-700 rounded text-sm font-semibold hover:bg-indigo-50 shadow-sm text-center"
              >
                Open CRM →
              </a>
            ) : (
              <a
                href="mailto:support@velaro.com?subject=Enable Velaro CRM"
                className="px-4 py-2 bg-white/10 text-indigo-200 rounded-lg text-sm font-semibold hover:bg-white/20 text-center transition-colors"
              >
                Contact Velaro to enable
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Why Velaro CRM — the differentiator */}
      <SectionCard title="Why use Velaro CRM instead of an external tool?" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              icon: "Zap",
              title: "Zero latency, zero API calls",
              body: "Skills run in-process inside Velaro. No network round-trips to Salesforce or HubSpot. Deal created in milliseconds, not seconds."
            },
            {
              icon: "Crosshairs",
              title: "Buying signals are native",
              body: "Velaro detects BUDGET, TIMELINE, READY_TO_BUY, and 7 other signal types in real time and writes them to your CRM automatically. No external tool can do this."
            },
            {
              icon: "Lock",
              title: "Zero credential exposure",
              body: "No OAuth tokens to store, rotate, or revoke. No third-party API keys. Data lives in your Velaro database, governed by the same security model as the rest of your account."
            },
            {
              icon: "DollarSign",
              title: "No third-party CRM needed",
              body: "No Salesforce or HubSpot license required. The full CRM — deals, pipeline, contacts, companies, activities, notes, products — runs natively inside Velaro."
            },
            {
              icon: "Bot",
              title: "AI-first design",
              body: "Every CRM action is a first-class AI skill. Bots can create deals, log calls, search contacts, and build quotes mid-conversation without any workflow node configuration."
            },
            {
              icon: "BarChart2",
              title: "Conversation-native data",
              body: "Deals are auto-linked to the conversation and contact that created them. The full chat transcript is available as context on every deal. No manual logging, no copy-paste."
            }
          ].map((item, i) => (
            <div key={i} className="flex gap-3 p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
              <div className="flex-shrink-0"><IconByName name={item.icon} className="w-6 h-6" /></div>
              <div>
                <div className="text-sm font-semibold text-zinc-800 mb-1">{item.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Capabilities */}
      <CapabilitiesPanel />

      {/* AI Tools */}
      <SectionCard
        title="AI Tools"
        description="Every tool is available to any AI workflow node on this site. No configuration required — the AI decides when to use CRM based on conversation context."
        className="mb-4"
        action={enableNativeCrm ? (
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 whitespace-nowrap font-medium shadow-sm"
          >
            Launch Starter Wizard
          </button>
        ) : undefined}
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">24 tools</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${enableNativeCrm ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
            {enableNativeCrm ? "Active" : "Requires EnableNativeCrm"}
          </span>
        </div>

        {!enableNativeCrm && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-4">
            <div className="text-sm font-medium text-amber-800 mb-1">Velaro CRM is not enabled on your account</div>
            <div className="text-xs text-amber-700">
              Velaro needs to enable this for your account, which may require an upgrade. Contact your Velaro representative or{" "}
              <a href="mailto:support@velaro.com?subject=Enable Velaro CRM" className="underline">support@velaro.com</a>{" "}
              to get started. All 24 AI tools will become available immediately once enabled.
            </div>
          </div>
        )}

        <Divider />
        <div className="pt-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Available AI tools</div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">24 tools · 6 groups</span>
          </div>

          {AI_TOOL_GROUPS.map((group) => (
            <div key={group} className="mb-5">
              <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">{group}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                  <div
                    key={tool.name}
                    className={`flex gap-3 p-3 rounded-lg border ${
                      enableNativeCrm ? "border-indigo-200 bg-indigo-50/50" : "border-zinc-200 bg-zinc-50"
                    }`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                      enableNativeCrm ? "bg-indigo-500" : "bg-zinc-300"
                    }`}>
                      {enableNativeCrm && (
                        <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                          <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-zinc-700">{tool.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{tool.description}</div>
                      <code className="text-[10px] text-zinc-400 mt-1 block">{tool.name}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Competitive comparison */}
      <SectionCard title="CRM AI Skill Comparison" description="Velaro CRM now has full AI parity with every external CRM we support. If your team doesn't use an external CRM, you get the same capability — included." className="mb-4">

        <div className="rounded-xl border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">CRM</th>
                <th className="px-4 py-3 text-center font-semibold">AI Skills</th>
                <th className="px-4 py-3 text-center font-semibold">Buying Signals</th>
                <th className="px-4 py-3 text-center font-semibold">Setup Required</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {[
                { name: "Velaro CRM (built-in)", skills: "27", signals: true, setup: "None", highlight: true },
                { name: "HubSpot", skills: "25", signals: false, setup: "OAuth", highlight: false },
                { name: "Salesforce", skills: "25", signals: false, setup: "OAuth + tokens", highlight: false },
                { name: "Dynamics 365", skills: "24", signals: false, setup: "Azure App", highlight: false },
                { name: "NetSuite", skills: "24", signals: false, setup: "SuiteTalk config", highlight: false },
                { name: "Zoho CRM", skills: "25", signals: false, setup: "OAuth", highlight: false },
                { name: "Pipedrive", skills: "23", signals: false, setup: "API key", highlight: false },
                { name: "SAP Business One", skills: "34", signals: false, setup: "API credentials", highlight: false }
              ].map((row, i) => (
                <tr key={i} className={row.highlight ? "bg-indigo-50" : i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                  <td className={`px-4 py-3 font-medium ${row.highlight ? "text-indigo-700" : "text-zinc-700"}`}>
                    {row.highlight && <span className="mr-1.5">⭐</span>}
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-semibold ${row.highlight ? "text-indigo-700" : "text-zinc-600"}`}>{row.skills}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.signals
                      ? <Check className="w-4 h-4 text-green-600 inline-block" />
                      : <span className="text-zinc-300 text-base">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-zinc-500">{row.setup}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-400 mt-2">
          Buying Signals = real-time AI detection of intent during the conversation (BUDGET, TIMELINE, READY_TO_BUY, COMPETITOR, PAIN_POINT, etc.). Unique to Velaro CRM.
        </p>
      </SectionCard>

      {/* Activity Log */}
      <SectionCard title="Activity Log">
        <IntegrationActivityLog integrationSlug="NativeCrm" />
      </SectionCard>
    </div>
  );
}
